import { TipoTarifacaoEnum } from '@shared/enums/financas/tipo-tarifacao.enum';
import { IntermediadorCondicaoComercialSelectStruct } from '@shared/services/structs/financas/intermediador-condicao-comercial-select.struct';
import { IntermediadorPagamentoListaStruct } from '@shared/services/structs/financas/intermediador-pagamento-lista.struct';
import { UtilService } from '@shared/services/util.service';
import { proximoDiaUtil } from './nfe-pagamento.dia-util';
import { somarDias } from './nfe-pagamento.parcelas';

/**
 * Prazo, taxa e tarifa da condição comercial do intermediador aplicados às parcelas de um recebimento. Cópia de
 * `venda-pagamento.intermediador.ts` (fluxo irmão da venda); espelho de `IntermediadorCalculoHelper` do Finanças:
 * a tela antecipa os números que o lançamento do financeiro vai gravar. Exemplos do artefato da PO: Visa 3x de R$ 37,50, taxa 3%, prazo 31 dias, tarifa R$ 2,00
 * -> parcelas 12,50 / taxa 0,38 / líquido 12,12, vencendo +31/+62/+93; no dia do pagamento a tarifa rateia
 * 0,66 / 0,66 / 0,68 e os líquidos ficam 11,46 / 11,46 / 11,44.
 */

export interface ParcelaIntermediadorCalculada {
    numeroParcela: number;
    vencimento: Date;
    valor: number;
    taxa: number;
    tarifa: number;
    liquido: number;
}

/**
 * Condição comercial da forma para a bandeira do recebimento (ou a sem bandeira) que cobre N parcelas: entre as
 * que cobrem, a de menor "parcelado até" é a faixa certa (1x a 3%, 6x a 4%...). `null`: sem condição.
 */
export function resolverCondicao(
    intermediador: IntermediadorPagamentoListaStruct | null | undefined,
    idFormaRecebimento: number | null | undefined,
    idBandeira: number | null | undefined,
    quantidadeParcelas: number,
): IntermediadorCondicaoComercialSelectStruct | null {
    const daForma = (intermediador?.condicoesComerciais ?? [])
        .filter((c) => c.idFormaRecebimento === idFormaRecebimento && c.parceladoAte >= quantidadeParcelas)
        .sort((a, b) => a.parceladoAte - b.parceladoAte);

    return (idBandeira != null ? daForma.find((c) => c.idBandeira === idBandeira) : undefined)
        ?? daForma.find((c) => c.idBandeira == null)
        ?? null;
}

/** Até quantas parcelas o intermediador aceita nesta forma (e bandeira): o maior "parcelado até" das condições. */
export function maximoDeParcelas(
    intermediador: IntermediadorPagamentoListaStruct | null | undefined,
    idFormaRecebimento: number | null | undefined,
    idBandeira: number | null | undefined,
): number {
    const daForma = (intermediador?.condicoesComerciais ?? [])
        .filter((c) => c.idFormaRecebimento === idFormaRecebimento && (c.idBandeira == null || c.idBandeira === idBandeira));

    return daForma.reduce((max, c) => Math.max(max, c.parceladoAte), 0);
}

/**
 * Parcela i vence em data de emissão + i × prazo (próximo dia útil quando o intermediador pede); taxa por parcela
 * sobre o valor dela; a tarifa só entra aqui na tarifação no dia do pagamento, rateada.
 */
export function calcularParcelasIntermediador(
    parcelas: { numeroParcela: number; valor: number }[],
    dataEmissao: Date,
    prazoDias: number,
    taxaPercentual: number,
    tarifaRateada: number,
    usarProximoDiaUtil: boolean,
): ParcelaIntermediadorCalculada[] {
    const tarifas = ratearTarifa(tarifaRateada, parcelas.length);

    return [...parcelas]
        .sort((a, b) => a.numeroParcela - b.numeroParcela)
        .map((p, indice) => {
            const vencimento = somarDias(dataEmissao, (indice + 1) * prazoDias);
            const valor = UtilService.arredondarAbnt(Number(p.valor) || 0, 2);
            const taxa = UtilService.arredondarAbnt(valor * (taxaPercentual || 0) / 100, 2);
            const tarifa = tarifas[indice];

            return {
                numeroParcela: p.numeroParcela,
                vencimento: usarProximoDiaUtil ? proximoDiaUtil(vencimento) : vencimento,
                valor,
                taxa,
                tarifa,
                liquido: UtilService.arredondarAbnt(valor - taxa - tarifa, 2),
            };
        });
}

/**
 * Rateio da tarifa em centavos com o resto na última parcela. Quando a cota por parcela fica abaixo de R$ 0,01,
 * vai R$ 0,01 para as primeiras até esgotar e as demais ficam sem (R$ 0,02 em 3x -> 0,01 / 0,01 / 0,00).
 */
export function ratearTarifa(tarifa: number, quantidadeParcelas: number): number[] {
    const rateio = new Array<number>(Math.max(0, quantidadeParcelas)).fill(0);

    if (!(tarifa > 0) || quantidadeParcelas <= 0) return rateio;

    const centavos = Math.round(UtilService.arredondarAbnt(tarifa, 2) * 100);
    const cota = Math.floor(centavos / quantidadeParcelas);

    if (cota === 0) {
        for (let i = 0; i < centavos && i < quantidadeParcelas; i++) rateio[i] = 0.01;

        return rateio;
    }

    for (let i = 0; i < quantidadeParcelas; i++) rateio[i] = cota / 100;
    rateio[quantidadeParcelas - 1] = UtilService.arredondarAbnt(rateio[quantidadeParcelas - 1] + (centavos - cota * quantidadeParcelas) / 100, 2);

    return rateio;
}

/**
 * Data da tarifa lançada separado, contada do dia do lançamento: dia específico é a próxima ocorrência do
 * `diaDebito` (o próprio dia vale; dia já passado vai para o mês seguinte; dia inexistente no mês é o dia
 * seguinte ao último); dia da transação é o próprio lançamento; dia seguinte é lançamento + 1.
 * `null`: a tarifação não gera saída separada (sem taxas ou no dia do pagamento).
 */
export function dataTarifaSeparada(
    tipo: number | null | undefined,
    diaDebito: number | null | undefined,
    dataLancamento: Date,
    usarProximoDiaUtil: boolean,
): Date | null {
    const lancamento = new Date(dataLancamento.getFullYear(), dataLancamento.getMonth(), dataLancamento.getDate());
    let data: Date | null;

    switch (tipo) {
        case TipoTarifacaoEnum.DiaDaTransacao: data = lancamento; break;
        case TipoTarifacaoEnum.DiaSeguinte: data = somarDias(lancamento, 1); break;
        case TipoTarifacaoEnum.EmDiaEspecifico: data = proximaOcorrenciaDoDia(diaDebito ?? lancamento.getDate(), lancamento); break;
        default: data = null;
    }

    return data && usarProximoDiaUtil ? proximoDiaUtil(data) : data;
}

function proximaOcorrenciaDoDia(dia: number, aPartirDe: Date): Date {
    const mes = aPartirDe.getDate() <= dia ? aPartirDe : new Date(aPartirDe.getFullYear(), aPartirDe.getMonth() + 1, 1);
    const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();

    return dia <= ultimoDia
        ? new Date(mes.getFullYear(), mes.getMonth(), dia)
        : new Date(mes.getFullYear(), mes.getMonth(), ultimoDia + 1);
}
